import React, { useState } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { CheckCircle2, Target, MapPin, CreditCard, Loader2 } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const SubscriptionCheckout = ({ isOpen, onClose, selectedPlan, onSuccess }) => {
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  
  const [formData, setFormData] = useState({
    goal: 'balanced',
    street: '',
    apartment: '',
    city: '',
    state: '',
    zip_code: '',
    payment_method: 'card',
    dietary_restrictions: [],
    favorite_cuisines: []
  });

  const goals = [
    {
      id: 'weight_loss',
      name: 'Weight Loss',
      description: 'Low carb, high protein meals',
      icon: '🎯',
      calories: '400-500 cal/meal'
    },
    {
      id: 'muscle_gain',
      name: 'Muscle Gain',
      description: 'High protein, balanced macros',
      icon: '💪',
      calories: '600-700 cal/meal'
    },
    {
      id: 'balanced',
      name: 'Balanced Diet',
      description: 'Well-rounded nutrition',
      icon: '⚖️',
      calories: '500-600 cal/meal'
    }
  ];

  const handleSubmit = async () => {
    // Validate
    if (!formData.street || !formData.city || !formData.state || !formData.zip_code) {
      toast.error('Please fill in all required address fields');
      return;
    }

    try {
      setLoading(true);
      
      // Get frontend origin for dynamic success/cancel URLs
      const originUrl = window.location.origin;
      
      // Call Stripe checkout session creation
      const response = await axios.post(
        `${API}/api/subscriptions/create-checkout-session`,
        {
          plan_type: selectedPlan.id,
          origin_url: originUrl
        },
        { withCredentials: true }
      );

      if (response.data.success && response.data.checkout_url) {
        // Store delivery details in localStorage for post-payment setup
        localStorage.setItem('pending_subscription_details', JSON.stringify({
          goal: formData.goal,
          delivery_address: {
            street: formData.street,
            apartment: formData.apartment,
            city: formData.city,
            state: formData.state,
            zip_code: formData.zip_code
          },
          dietary_restrictions: formData.dietary_restrictions,
          favorite_cuisines: formData.favorite_cuisines
        }));
        
        // Redirect to Stripe Checkout
        window.location.href = response.data.checkout_url;
      } else {
        throw new Error('Failed to create checkout session');
      }
    } catch (error) {
      console.error('Subscription error:', error);
      const errorMsg = error.response?.data?.detail || 'Failed to create checkout session';
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const renderStep1 = () => (
    <div className="space-y-6">
      <div className="text-center">
        <Target className="w-12 h-12 mx-auto text-indigo-600 mb-4" />
        <h3 className="text-xl font-semibold mb-2">Choose Your Goal</h3>
        <p className="text-gray-600 text-sm">We'll tailor your meal plan to help you achieve it</p>
      </div>

      <RadioGroup value={formData.goal} onValueChange={(value) => setFormData({...formData, goal: value})}>
        {goals.map((goal) => (
          <label
            key={goal.id}
            htmlFor={goal.id}
            className={`flex items-center p-4 border-2 rounded-lg cursor-pointer transition-all ${
              formData.goal === goal.id
                ? 'border-indigo-500 bg-indigo-50'
                : 'border-gray-200 hover:border-indigo-300'
            }`}
          >
            <RadioGroupItem value={goal.id} id={goal.id} className="mr-4" />
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-1">
                <span className="text-2xl">{goal.icon}</span>
                <span className="font-semibold text-gray-900">{goal.name}</span>
              </div>
              <p className="text-sm text-gray-600">{goal.description}</p>
              <p className="text-xs text-indigo-600 mt-1">{goal.calories}</p>
            </div>
          </label>
        ))}
      </RadioGroup>

      <Button onClick={() => setStep(2)} className="w-full bg-indigo-600 hover:bg-indigo-700 h-12">
        Continue to Delivery Address
      </Button>
    </div>
  );

  const renderStep2 = () => (
    <div className="space-y-6">
      <div className="text-center">
        <MapPin className="w-12 h-12 mx-auto text-indigo-600 mb-4" />
        <h3 className="text-xl font-semibold mb-2">Delivery Address</h3>
        <p className="text-gray-600 text-sm">Where should we deliver your meals?</p>
      </div>

      <div className="space-y-4">
        <div>
          <Label htmlFor="street">Street Address *</Label>
          <Input
            id="street"
            value={formData.street}
            onChange={(e) => setFormData({...formData, street: e.target.value})}
            placeholder="123 Main Street"
            required
          />
        </div>

        <div>
          <Label htmlFor="apartment">Apartment / Unit (Optional)</Label>
          <Input
            id="apartment"
            value={formData.apartment}
            onChange={(e) => setFormData({...formData, apartment: e.target.value})}
            placeholder="Apt 4B"
          />
        </div>

        <div className="grid grid-cols-3 gap-4">
          <div className="col-span-2">
            <Label htmlFor="city">City *</Label>
            <Input
              id="city"
              value={formData.city}
              onChange={(e) => setFormData({...formData, city: e.target.value})}
              placeholder="New York"
              required
            />
          </div>
          <div>
            <Label htmlFor="state">State *</Label>
            <Input
              id="state"
              value={formData.state}
              onChange={(e) => setFormData({...formData, state: e.target.value.toUpperCase()})}
              placeholder="NY"
              maxLength={2}
              required
            />
          </div>
        </div>

        <div>
          <Label htmlFor="zip">Zip Code *</Label>
          <Input
            id="zip"
            value={formData.zip_code}
            onChange={(e) => setFormData({...formData, zip_code: e.target.value})}
            placeholder="10001"
            required
          />
        </div>
      </div>

      <div className="flex gap-3">
        <Button variant="outline" onClick={() => setStep(1)} className="flex-1">
          Back
        </Button>
        <Button onClick={() => setStep(3)} className="flex-1 bg-indigo-600 hover:bg-indigo-700">
          Continue to Payment
        </Button>
      </div>
    </div>
  );

  const renderStep3 = () => (
    <div className="space-y-6">
      <div className="text-center">
        <CreditCard className="w-12 h-12 mx-auto text-indigo-600 mb-4" />
        <h3 className="text-xl font-semibold mb-2">Payment Method</h3>
        <p className="text-gray-600 text-sm">Choose how you'd like to pay</p>
      </div>

      <RadioGroup value={formData.payment_method} onValueChange={(value) => setFormData({...formData, payment_method: value})}>
        <label
          htmlFor="card"
          className={`flex items-center p-4 border-2 rounded-lg cursor-pointer ${
            formData.payment_method === 'card'
              ? 'border-indigo-500 bg-indigo-50'
              : 'border-gray-200 hover:border-indigo-300'
          }`}
        >
          <RadioGroupItem value="card" id="card" className="mr-4" />
          <div className="flex-1">
            <p className="font-semibold text-gray-900">Credit / Debit Card</p>
            <p className="text-sm text-gray-600">Secure weekly billing via Stripe</p>
          </div>
        </label>
      </RadioGroup>

      <Card className="bg-blue-50 border-blue-200">
        <CardContent className="p-4 text-sm text-blue-900">
          <strong>🔒 Secure Payment:</strong> You'll be redirected to Stripe to complete your payment securely. Stripe test mode is active.
        </CardContent>
      </Card>

      {/* Order Summary */}
      <Card className="bg-gray-50">
        <CardContent className="p-4">
          <h4 className="font-semibold text-gray-900 mb-3">Order Summary</h4>
          <div className="space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-gray-600">Plan</span>
              <span className="font-medium">{selectedPlan.name}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-gray-600">Goal</span>
              <span className="font-medium capitalize">{formData.goal.replace('_', ' ')}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-gray-600">Meals/Week</span>
              <span className="font-medium">{selectedPlan.mealsPerWeek}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-gray-600">First Delivery</span>
              <span className="font-medium">Next Monday</span>
            </div>
            <div className="border-t pt-2 mt-2 flex justify-between">
              <span className="font-semibold text-gray-900">Weekly Total</span>
              <span className="font-bold text-xl text-indigo-600">${selectedPlan.price}</span>
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="flex gap-3">
        <Button variant="outline" onClick={() => setStep(2)} className="flex-1" disabled={loading}>
          Back
        </Button>
        <Button 
          onClick={handleSubmit} 
          className="flex-1 bg-indigo-600 hover:bg-indigo-700 h-12"
          disabled={loading}
        >
          {loading ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Redirecting to Stripe...
            </>
          ) : (
            <>
              <CheckCircle2 className="w-4 h-4 mr-2" />
              Proceed to Payment
            </>
          )}
        </Button>
      </div>

      <p className="text-xs text-gray-500 text-center">
        By activating, you agree to our terms. Cancel anytime.
      </p>
    </div>
  );

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <div className="flex items-center justify-between">
            <div>
              <DialogTitle>Complete Your Subscription</DialogTitle>
              <DialogDescription>
                {selectedPlan.name} Plan - ${selectedPlan.price}/week
              </DialogDescription>
            </div>
            <Badge variant="secondary" className="text-sm">
              Step {step} of 3
            </Badge>
          </div>
        </DialogHeader>

        {/* Progress Bar */}
        <div className="flex gap-2 mb-6">
          {[1, 2, 3].map((s) => (
            <div
              key={s}
              className={`h-2 flex-1 rounded-full transition-all ${
                s <= step ? 'bg-indigo-600' : 'bg-gray-200'
              }`}
            />
          ))}
        </div>

        {/* Step Content */}
        {step === 1 && renderStep1()}
        {step === 2 && renderStep2()}
        {step === 3 && renderStep3()}
      </DialogContent>
    </Dialog>
  );
};

export default SubscriptionCheckout;
